import React from 'react';
import { Form, Col, Row, Typography, Input } from 'antd';
import { TicketField, TicketFormResponse } from '@/types';
import { useTranslation } from 'react-i18next';
import { entityFieldList, issueFieldList } from '@/constants';
import { FieldRenderer } from './FieldRenderer';
import Uploader from './Uploader';

const { TextArea } = Input;

interface Props {
  ticketForm?: TicketFormResponse;
  disableEntityId: boolean;
}

const TicketFormBuilder: React.FC<Props> = ({ ticketForm, disableEntityId }) => {
  const { t } = useTranslation();

  const checkIsOptionalField = (field: TicketField) => {
    const isDescription = field.title === 'textarea';

    const isIssueField =
      issueFieldList.findIndex((issueFieldId) => issueFieldId === field.id) !== -1;

    return isDescription || isIssueField;
  };

  const renderFormItems = (fields?: TicketField[]) => {
    return (
      <Row gutter={16}>
        {fields?.map((field) => {
          const isFullWidth = field.type === 'textarea';
          const isRequired = field.required;
          const isHiddenField = field.type === 'hidden';

          // Render hidden Form.Item for uid field (not visible but included in form values)
          if (isHiddenField) {
            if (field.key === 'uid') {
              return (
                <Form.Item key={field.id} name={String(field.id)} hidden>
                  <Input type='hidden' />
                </Form.Item>
              );
            } else {
              return null;
            }
          }

          return (
            <Col
              key={field.id}
              className='relative flex flex-col gap-2'
              span={isFullWidth ? 24 : 12}
            >
              {<Typography.Text>{field.title_in_portal}</Typography.Text>}
              <Form.Item
                name={String(field.id)}
                rules={[
                  {
                    required: checkIsOptionalField(field) ? false : isRequired,
                    message: t('this_field_is_required'),
                  },
                ]}
                initialValue={field.title === 'Priority' ? 'normal' : undefined}
              >
                <FieldRenderer
                  key={field.id}
                  field={field}
                  disabled={entityFieldList.includes(field.id)}
                />
              </Form.Item>
            </Col>
          );
        })}

        {/* Static Description and Attachments - always shown for all forms */}
        <Col span={24} className='relative flex flex-col gap-2'>
          <Typography.Text>{t('description')}</Typography.Text>
          <Form.Item name='description'>
            <TextArea
              rows={4}
              placeholder={t('enter_description')}
              variant='borderless'
              className='rounded-lg border border-solid border-customGrayShade4'
            />
          </Form.Item>
          <Form.Item name='attachments'>
            <Uploader />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  return <>{renderFormItems(ticketForm?.fields)}</>;
};

export default TicketFormBuilder;
